import React, { useState, useEffect } from 'react';
import { Checkbox, Input, Button, Space, Divider } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { AgentType } from '../../../types/krybot';
import { CAPABILITIES_EXAMPLES, getCapabilitiesExample } from '../../../constants/krybotSchemas';

interface CapabilitiesHelperProps {
  value: Record<string, boolean> | string;
  onChange: (value: Record<string, boolean>) => void;
  agentType?: AgentType;
  disabled?: boolean;
}

const COMMON_CAPABILITIES = [
  { key: 'artifact-read', label: 'Artifact Read', description: 'Read/view artifacts' },
  { key: 'artifact-write', label: 'Artifact Write', description: 'Create and update artifacts' },
  { key: 'artifact-delete', label: 'Artifact Delete', description: 'Delete artifacts' },
  { key: 'process-read', label: 'Process Read', description: 'Read/view processes' },
  { key: 'process-write', label: 'Process Write', description: 'Create and update processes' },
  { key: 'process-execute', label: 'Process Execute', description: 'Execute processes' },
  { key: 'process-delete', label: 'Process Delete', description: 'Delete processes' },
  { key: 'thread-read', label: 'Thread Read', description: 'Read chat threads' },
  { key: 'thread-write', label: 'Thread Write', description: 'Create and update threads' },
  { key: 'thread-delete', label: 'Thread Delete', description: 'Delete threads' },
  { key: 'chat', label: 'Chat', description: 'Engage in chat conversations' },
  { key: 'validation', label: 'Validation', description: 'Perform validation operations' },
  { key: 'approval_request', label: 'Approval Request', description: 'Request approvals' },
];

export const CapabilitiesHelper: React.FC<CapabilitiesHelperProps> = ({
  value,
  onChange,
  agentType,
  disabled = false,
}) => {
  // Parse value - can be object or JSON string
  const parseValue = (val: Record<string, boolean> | string): Record<string, boolean> => {
    if (typeof val === 'string') {
      try {
        return JSON.parse(val) || {};
      } catch {
        return {};
      }
    }
    return val || {};
  };

  const [capabilities, setCapabilities] = useState<Record<string, boolean>>(parseValue(value));
  const [customCapabilities, setCustomCapabilities] = useState<Array<{ key: string; value: boolean }>>([]);
  const [newCustomKey, setNewCustomKey] = useState('');

  useEffect(() => {
    const parsed = parseValue(value);
    setCapabilities(parsed);
    
    // Extract custom capabilities (not in COMMON_CAPABILITIES)
    const commonKeys = new Set(COMMON_CAPABILITIES.map(c => c.key));
    const custom: Array<{ key: string; value: boolean }> = [];
    Object.entries(parsed).forEach(([key, val]) => {
      if (!commonKeys.has(key)) {
        custom.push({ key, value: val });
      }
    });
    setCustomCapabilities(custom);
  }, [value]);

  const handleCommonCapabilityChange = (key: string, checked: boolean) => {
    if (disabled) return;
    const updated = { ...capabilities, [key]: checked };
    setCapabilities(updated);
    onChange(updated);
  };

  const handleCustomCapabilityChange = (key: string, checked: boolean) => {
    if (disabled) return;
    const updated = { ...capabilities, [key]: checked };
    setCapabilities(updated);
    onChange(updated);
  };

  const handleAddCustom = () => {
    if (disabled || !newCustomKey.trim()) return;
    const key = newCustomKey.trim();
    if (!Object.prototype.hasOwnProperty.call(capabilities, key)) {
      const updated = { ...capabilities, [key]: true };
      setCapabilities(updated);
      setCustomCapabilities([...customCapabilities, { key, value: true }]);
      setNewCustomKey('');
      onChange(updated);
    }
  };

  const handleRemoveCustom = (key: string) => {
    if (disabled) return;
    const updated = { ...capabilities };
    delete updated[key];
    setCapabilities(updated);
    setCustomCapabilities(customCapabilities.filter(c => c.key !== key));
    onChange(updated);
  };

  const handleLoadExample = (exampleKey: keyof typeof CAPABILITIES_EXAMPLES) => {
    if (disabled) return;
    const example = CAPABILITIES_EXAMPLES[exampleKey];
    setCapabilities(example);
    onChange(example);
  };

  const handleLoadSuggested = () => {
    if (disabled) return;
    const suggested = getCapabilitiesExample(agentType);
    setCapabilities(suggested);
    onChange(suggested);
  };

  return (
    <div className="capabilities-helper">
      <div className="capabilities-common">
        <div className="capabilities-section-header">
          <h4>Common Capabilities</h4>
          <Space>
            <Button size="small" onClick={handleLoadSuggested} disabled={disabled}>
              Load Suggested ({agentType || 'default'})
            </Button>
            <Button size="small" onClick={() => handleLoadExample('minimal')} disabled={disabled}>
              Minimal
            </Button>
            <Button size="small" onClick={() => handleLoadExample('fullAccess')} disabled={disabled}>
              Full Access
            </Button>
          </Space>
        </div>
        <div className="capabilities-checkboxes">
          {COMMON_CAPABILITIES.map((cap) => (
            <div key={cap.key} className="capability-item">
              <Checkbox
                checked={capabilities[cap.key] || false}
                onChange={(e) => handleCommonCapabilityChange(cap.key, e.target.checked)}
                disabled={disabled}
              >
                <span className="capability-label">{cap.label}</span>
                <span className="capability-key">({cap.key})</span>
              </Checkbox>
              <span className="capability-description">{cap.description}</span>
            </div>
          ))}
        </div>
      </div>

      <Divider />

      <div className="capabilities-custom">
        <h4>Custom Capabilities</h4>
        <div className="capabilities-add-custom">
          <Input
            placeholder="Enter custom capability key"
            value={newCustomKey}
            onChange={(e) => setNewCustomKey(e.target.value)}
            onPressEnter={handleAddCustom}
            disabled={disabled}
            style={{ width: 300, marginRight: 8 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddCustom}
            disabled={disabled || !newCustomKey.trim()}
          >
            Add
          </Button>
        </div>
        {customCapabilities.length > 0 && (
          <div className="capabilities-custom-list">
            {customCapabilities.map((custom) => (
              <div key={custom.key} className="capability-item">
                <Checkbox
                  checked={custom.value}
                  onChange={(e) => handleCustomCapabilityChange(custom.key, e.target.checked)}
                  disabled={disabled}
                >
                  <span className="capability-label">{custom.key}</span>
                </Checkbox>
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveCustom(custom.key)}
                  disabled={disabled}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="capabilities-json-preview">
        <details>
          <summary>JSON Preview</summary>
          <pre>{JSON.stringify(capabilities, null, 2)}</pre>
        </details>
      </div>
    </div>
  );
};

